import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../../auth/useAuth0';
import { useBusinessContext } from '../../../../context/BusinessContext';
import { useAlert } from '../../../../context/alert/AlertContext';
import { useConfirm } from '../../../../context/confirm/ConfirmContext';
import LoadingComponent from '../../../commons/LoadingComponent';
import { Category } from '../../../../model/category/Category';
import { CategoryInput } from '../../../../service/category/CategoryInput';
import {
    deleteCategory,
    getCategoriesByBusinessId,
    saveCategory,
} from '../../../../service/category/category-service';
import Drawer from '../../../commons/drawer/Drawer';
import Pagination from '../../../commons/pagination/Pagination';
import { uploadImage, mediaUrl } from '../../../../service/media/media-service';
import { findBusinessSettingByBusinessId } from '../../../../service/business/business-service';

type CategoryDraft = CategoryInput & { tagsText?: string };

const emptyDraft = (businessId: number): CategoryDraft => ({
    businessId,
    name: '',
    description: '',
    color: '#888888',
    imageUrl: '',
    isActive: true,
    isHealthData: null,
    tags: [],
    tagsText: '',
});

const CategoriesTab: React.FC = () => {
    const { t } = useTranslation();
    const confirmDialog = useConfirm();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();

    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [draft, setDraft] = useState<CategoryDraft | null>(null);
    const [saving, setSaving] = useState(false);

    /** Prontuário master switch of the business — when ON the health question
     *  below becomes mandatory and unanswered categories get flagged. */
    const [healthMode, setHealthMode] = useState(false);

    /** Pagination — keeps the list manageable for long taxonomies. */
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    /** Free-text filter on category name / tags (case-insensitive). */
    const [search, setSearch] = useState('');

    const businessId = Number(selectedBusiness?.id) || 0;

    const loadCategories = useCallback(async () => {
        if (!businessId) {
            setCategories([]); setLoading(false); return;
        }
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            // Scoped query — Mongo returns only this business's categories.
            const data = await getCategoriesByBusinessId(businessId, token);
            setCategories(data);
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('services.alerts.failLoadCategories'));
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently, setAlert, businessId, t]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    useEffect(() => {
        if (!businessId) return;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const settings = await findBusinessSettingByBusinessId(businessId, token);
                setHealthMode(!!settings?.healthMode);
            } catch {
                // Fail-safe: without the flag we still allow saving (module hidden).
                setHealthMode(false);
            }
        })();
    }, [businessId, getAccessTokenSilently]);

    const startCreate = () => {
        setSelectedId(null);
        setDraft(emptyDraft(businessId));
    };

    const startEdit = (c: Category) => {
        setSelectedId(c.id);
        setDraft({
            id: c.id,
            businessId: Number(c.businessId) || businessId,
            name: c.name ?? '',
            description: c.description ?? '',
            color: c.color ?? '#888888',
            imageUrl: c.imageUrl ?? '',
            isActive: c.isActive ?? true,
            isHealthData: c.isHealthData ?? null,
            tags: c.tags ?? [],
            tagsText: (c.tags ?? []).join(', '),
        });
    };

    const cancelEdit = () => {
        setSelectedId(null);
        setDraft(null);
    };

    const handleChange = <K extends keyof CategoryDraft>(field: K, value: CategoryDraft[K]) => {
        setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        if (!businessId) { setAlert('danger', t('services.alerts.selectBusiness')); return; }
        try {
            const token = await getAccessTokenSilently();
            const key = await uploadImage(file, `category/${businessId}`, token);
            handleChange('imageUrl', key);
        } catch (err) {
            setAlert('danger', err instanceof Error ? err.message : t('services.alerts.failUploadImage'));
        }
    };

    const handleSave = async () => {
        if (!draft) return;
        if (!draft.name || !draft.name.trim()) {
            setAlert('danger', t('services.alerts.categoryNameRequired'));
            return;
        }
        if (!businessId) {
            setAlert('danger', t('services.alerts.noBusinessSelected'));
            return;
        }
        // Prontuário: with healthMode ON the classification is mandatory —
        // no insecure default, the admin must answer consciously.
        if (healthMode && draft.isHealthData !== true && draft.isHealthData !== false) {
            setAlert('danger', t('services.alerts.healthDataRequired'));
            return;
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            const { tagsText, ...rest } = draft;
            const input: CategoryInput = {
                ...rest,
                businessId,
                tags: tagsText
                    ? tagsText.split(',').map((t) => t.trim()).filter(Boolean)
                    : [],
            };
            const saved = await saveCategory(input, token);
            setAlert('success', t('services.alerts.categorySaved', { name: saved.name }));
            // Close the drawer on success (don't re-open the editor).
            cancelEdit();
            await loadCategories();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('services.alerts.failSaveCategory'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!draft?.id) return;
        if (!(await confirmDialog({ title: t('common.confirmTitle'), message: t('services.alerts.confirmDeleteCategory', { name: draft.name }), danger: true, confirmLabel: t('common.delete') }))) return;
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            await deleteCategory(draft.id, token);
            setAlert('success', t('services.alerts.categoryDeleted'));
            cancelEdit();
            await loadCategories();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('services.alerts.failDeleteCategory'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <LoadingComponent />;

    const q = search.trim().toLowerCase();
    const filteredCategories = q
        ? categories.filter((c) =>
              (c.name ?? '').toLowerCase().includes(q) ||
              (c.tags ?? []).some((tag) => tag.toLowerCase().includes(q))
          )
        : categories;
    const totalPages = Math.max(1, Math.ceil(filteredCategories.length / perPage));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * perPage;
    const visibleCategories = filteredCategories.slice(
        pageStart,
        pageStart + perPage
    );

    return (
        <>
            <div className="list-card">
                <div className="list-header">
                    <h5 className="mb-0">{t('services.categoriesTitle')}</h5>
                    <div className="gs-list-search">
                        <span className="gs-list-search__icon" aria-hidden>
                            ⌕
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t('services.searchCategoriesPlaceholder')}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                        />
                        {search && (
                            <button
                                type="button"
                                className="gs-list-search__clear"
                                onClick={() => {
                                    setSearch('');
                                    setPage(1);
                                }}
                                aria-label={t('common.clear')}
                                title={t('common.clear')}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <button type="button" className="btn btn-primary btn-sm" onClick={startCreate} data-cy="category-add-btn">
                        {t('services.addNew')}
                    </button>
                </div>

                {healthMode && categories.some((c) => c.isHealthData == null) && (
                    <div className="alert alert-warning py-2 px-3 mb-2" data-cy="health-classify-banner">
                        {t('services.healthClassifyBanner')}
                    </div>
                )}

                {categories.length === 0 && (
                    <div className="empty-hint">{t('services.emptyCategories')}</div>
                )}

                {categories.length > 0 && filteredCategories.length === 0 && (
                    <div className="empty-hint">
                        {t('services.emptyCategoriesSearch', { search })}
                    </div>
                )}

                {visibleCategories.map((c) => (
                    <div
                        key={c.id}
                        className={`list-item ${selectedId === c.id ? 'selected' : ''}`}
                        onClick={() => startEdit(c)}
                        data-cy={`category-row-${c.id}`}
                    >
                        <div>
                            <span
                                className="color-swatch"
                                style={{ background: c.color ?? '#ccc' }}
                            />
                            <span className="name">{c.name || '(no name)'}</span>
                            {c.isHealthData === true && (
                                <span className="badge rounded-pill text-bg-primary ms-2">{t('services.healthBadge')}</span>
                            )}
                            {healthMode && c.isHealthData == null && (
                                <span className="badge rounded-pill text-bg-warning ms-2">{t('services.healthUnclassifiedBadge')}</span>
                            )}
                            <div className="meta">
                                {c.isActive === false
                                    ? t('services.categoryStatusInactive')
                                    : t('services.categoryStatusActive')}
                                {c.tags && c.tags.length > 0 && ` — ${c.tags.join(', ')}`}
                            </div>
                        </div>
                    </div>
                ))}

                <Pagination
                    page={safePage}
                    perPage={perPage}
                    totalItems={filteredCategories.length}
                    onPageChange={setPage}
                    onPerPageChange={(n) => {
                        setPerPage(n);
                        setPage(1);
                    }}
                    itemLabel="category"
                />
            </div>

            <Drawer
                open={!!draft}
                onClose={cancelEdit}
                title={draft?.id ? t('services.drawerEditCategory') : t('services.drawerNewCategory')}
                size="md"
                footer={
                    draft && (
                        <>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                                data-cy="category-save-btn"
                            >
                                {saving ? t('common.working') : t('common.save')}
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={cancelEdit}
                                disabled={saving}
                                data-cy="category-cancel-btn"
                            >
                                {t('common.cancel')}
                            </button>
                            {draft.id && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger ms-auto"
                                    onClick={handleDelete}
                                    disabled={saving}
                                    data-cy="category-delete-btn"
                                >
                                    {t('common.delete')}
                                </button>
                            )}
                        </>
                    )
                }
            >
                {draft && (
                    <form className="event-form" onSubmit={(e) => e.preventDefault()} data-cy="category-drawer">
                        <div className="form-group">
                            <label>{t('services.categoryName')} <span className="gs-required">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                value={draft.name ?? ''}
                                maxLength={100}
                                onChange={(e) => handleChange('name', e.target.value)}
                                data-cy="category-name-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>{t('services.categoryDescription')}</label>
                            <textarea
                                className="form-control"
                                rows={2}
                                value={draft.description ?? ''}
                                onChange={(e) => handleChange('description', e.target.value)}
                                data-cy="category-description-input"
                            />
                        </div>

                        <div className="gs-form-grid gs-form-grid--1-2">
                            <div className="form-group">
                                <label>{t('services.categoryColor')}</label>
                                <div className="gs-color-swatch-row">
                                    <label
                                        className="gs-color-swatch"
                                        style={{ background: draft.color ?? '#888888' }}
                                        title={t('services.pickColor')}
                                    >
                                        <input
                                            type="color"
                                            value={draft.color ?? '#888888'}
                                            onChange={(e) => handleChange('color', e.target.value)}
                                        />
                                    </label>
                                    <code className="gs-color-swatch__hex">
                                        {(draft.color ?? '#888888').toUpperCase()}
                                    </code>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{t('services.categoryImage')}</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control"
                                    onChange={handleImageUpload}
                                    style={{ marginBottom: '0.5rem' }}
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    value={draft.imageUrl ?? ''}
                                    onChange={(e) => handleChange('imageUrl', e.target.value)}
                                    placeholder={t('services.categoryImageUrlPlaceholder')}
                                />
                                {draft.imageUrl ? (
                                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                    <img src={mediaUrl(draft.imageUrl)} alt="preview"
                                        style={{ marginTop: '0.5rem', maxHeight: 80, borderRadius: 8 }} />
                                ) : null}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{t('services.categoryTags')}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={draft.tagsText ?? ''}
                                onChange={(e) => handleChange('tagsText', e.target.value)}
                                placeholder={t('services.categoryTagsPlaceholder')}
                                data-cy="category-tags-input"
                            />
                        </div>

                        <div className="form-group">
                            <label className="gs-inline-check">
                                <input
                                    type="checkbox"
                                    className="gs-inline-check__box"
                                    checked={!!draft.isActive}
                                    onChange={(e) => handleChange('isActive', e.target.checked)}
                                    data-cy="category-active-checkbox"
                                />
                                <span className="gs-inline-check__label">{t('services.categoryActive')}</span>
                            </label>
                        </div>

                        {healthMode && (
                            <div className="form-group" data-cy="category-health-question">
                                <label>
                                    {t('services.healthQuestion')} <span className="gs-required">*</span>
                                </label>
                                <div className="d-flex gap-4 mt-1">
                                    <label className="gs-inline-check">
                                        <input
                                            type="radio"
                                            name="category-health"
                                            className="gs-inline-check__box"
                                            checked={draft.isHealthData === true}
                                            onChange={() => handleChange('isHealthData', true)}
                                            data-cy="category-health-yes"
                                        />
                                        <span className="gs-inline-check__label">{t('common.yes')}</span>
                                    </label>
                                    <label className="gs-inline-check">
                                        <input
                                            type="radio"
                                            name="category-health"
                                            className="gs-inline-check__box"
                                            checked={draft.isHealthData === false}
                                            onChange={() => handleChange('isHealthData', false)}
                                            data-cy="category-health-no"
                                        />
                                        <span className="gs-inline-check__label">{t('common.no')}</span>
                                    </label>
                                </div>
                                <small className="text-muted">{t('services.healthQuestionHint')}</small>
                            </div>
                        )}

                    </form>
                )}
            </Drawer>
        </>
    );
};

export default CategoriesTab;
